import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, FormHelperText, Grid, TextField} from '@mui/material'
import {Formik, FormikErrors, yupToFormErrors} from 'formik'
import type {TFunction} from 'i18next'
import {useSnackbar} from 'notistack'
import * as Yup from 'yup'

import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {CreateAccountSubProps} from '@progyny/pages/CreateAccount/types'
import {
  calculateAge,
  formatDateToFormattedString,
  isoFormat,
  isValidDate,
  parseMemberBirthDay,
  StringDateParseError,
} from '@progyny/utils/dates'

export function applyBirthDateSubmitError(
  e: unknown,
  t: TFunction,
  enqueueSnackbar: (opts: {message: string; variant: 'error'}) => void
): void {
  if (e instanceof StringDateParseError) {
    enqueueSnackbar({
      message: t('create_account_birth_date_please_enter_valid_birth_date'),
      variant: 'error',
    })
    return
  }
  throw e
}

const today = new Date()

const BirthDate: React.ComponentType<CreateAccountSubProps> = ({
  navigator,
  callback,
  persistMemberData,
  initialValue,
  updateLoading,
  goBack,
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const isMobile = useResponsive('down', 'md')

  const Navigator = navigator

  const initialValues = parseMemberBirthDay(initialValue.birthDate)

  const invalidDayError = t(
    'create_account_birth_date_day_is_required_and_must'
  )
  const invalidMonthError = t(
    'create_account_birth_date_month_is_required_and_must'
  )
  const invalidYearError = t('setup_profile_pregnancy_date_year_required')
  const invalidAgeLowerError = t('create_account_birth_date_you_need_to_be_at')
  const invalidAgeUpperError = t(
    'create_account_birth_date_we_only_support_employees_70'
  )
  const invalidDateError = t('setup_profile_pregnancy_date_invalid_date')

  const BirthDateSchema = Yup.object().shape({
    day: Yup.number()
      .required(invalidDayError)
      .integer(invalidDayError)
      .min(1, invalidDayError)
      .max(31, invalidDayError),
    month: Yup.number()
      .required(invalidMonthError)
      .integer(invalidMonthError)
      .min(1, invalidMonthError)
      .max(12, invalidMonthError),
    year: Yup.number()
      .required(invalidYearError)
      .integer(invalidYearError)
      .min(1900, invalidAgeUpperError)
      .max(today.getFullYear() - 17, invalidAgeLowerError)
      .test({
        name: 'validDate',
        exclusive: true,
        message: invalidDateError,
        test(value): boolean {
          return isValidDate({
            day: this.parent.day,
            month: this.parent.month,
            year: value,
          })
        },
      })
      .test({
        name: 'tooYoung',
        exclusive: true,
        message: invalidAgeLowerError,
        test(value): boolean {
          const birthDate = new Date(
            isoFormat({
              year: value,
              month: this.parent.month,
              day: this.parent.day,
            })
          )
          const age = calculateAge(birthDate)
          return age > 17
        },
      })
      .test({
        name: 'tooOld',
        exclusive: true,
        message: invalidAgeUpperError,
        test(value) {
          const birthDate = new Date(
            isoFormat({
              year: value,
              month: this.parent.month,
              day: this.parent.day,
            })
          )
          const age = calculateAge(birthDate)
          return age < 71
        },
      }),
  })

  const dayElement = useRef<HTMLInputElement>()
  useEffect(() => {
    dayElement.current?.focus()
  }, [])

  function validateInitial(): FormikErrors<any> {
    try {
      BirthDateSchema.validateSync(initialValues)
      return {}
    } catch (err) {
      return yupToFormErrors(err)
    }
  }

  return (
    <Box sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}>
      <Formik
        initialValues={initialValues}
        validationSchema={BirthDateSchema}
        // if dateOfBirth is string, compose the string before calling onStepCompleted
        onSubmit={async (values) => {
          try {
            const {day, month, year} = values

            if (day && month && year) {
              const birthDate = formatDateToFormattedString(
                new Date(year!, month! - 1, day!)
              )
              await persistMemberData({birthDate})
              callback()
            }
          } catch (e) {
            applyBirthDateSubmitError(e, t, enqueueSnackbar)
          }
        }}
        validateOnChange
        initialErrors={validateInitial()}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          isValid,
          handleBlur,
          handleChange,
          submitForm,
        }) => {
          return (
            <>
              <Box mt={4}>
                <Grid container spacing={2} justifyContent={'center'}>
                  <Grid xs={4} sm={2} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_day')}
                      id='day'
                      name='day'
                      value={values.day}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isMobile && touched.day && !!errors.day}
                      helperText={
                        isMobile && touched.day ? <>{errors.day}</> : ''
                      }
                      inputRef={dayElement}
                      autoComplete={'bday-day'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '2'}}
                    />
                  </Grid>
                  <Grid xs={4} sm={3} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_month')}
                      id='month'
                      name='month'
                      value={values.month}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isMobile && touched.month && !!errors.month}
                      helperText={
                        isMobile && touched.month ? <>{errors.month}</> : ''
                      }
                      autoComplete={'bday-month'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '2'}}
                    />
                  </Grid>
                  <Grid xs={4} sm={3} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_year')}
                      id='year'
                      name='year'
                      value={values.year}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isMobile && touched.year && !!errors.year}
                      helperText={
                        isMobile && touched.year ? <>{errors.year}</> : ''
                      }
                      autoComplete={'bday-year'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '4'}}
                    />
                  </Grid>
                </Grid>
              </Box>
              {!isMobile && dirty && !isValid && (
                <Box>
                  <FormHelperText error={!isValid}>
                    <>
                      {(touched.day && errors.day) ||
                        (touched.month && errors.month) ||
                        ((values.year || -1) > 999 && errors.year)}
                    </>
                  </FormHelperText>
                </Box>
              )}
              <MobileStickyActions>
                <Navigator
                  submitDataRole={'birthdate'}
                  label={t('common_continue')}
                  onNextButtonClick={submitForm}
                  buttonDisable={!isValid || updateLoading}
                  onPrevButtonClick={goBack}
                  backButtonType='outlined'
                  fullWidth={isMobile}
                />
              </MobileStickyActions>
            </>
          )
        }}
      </Formik>
    </Box>
  )
}

export default BirthDate
